import { CardCvcElement, CardExpiryElement, CardNumberElement } from '@stripe/react-stripe-js';
import { useEffect, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import Button from '../../../../components/Button/Button';
import {
  ErrorMessageStyled,
  FieldWrapperStyled,
  InputLabelStyled,
} from '../../../../components/FormikInput/FormikInput.styled';
import Heading from '../../../../components/Heading/Heading.styled';
import Popup from '../../../../components/Popup/Popup';
import usePaymentForm from '../../hooks/usePaymentForm';
import { ControlsWrapperStyled, FormRowStyled } from '../../PaymentDetailsPage.styled';
import { PaymentCardPropsT } from './PaymentCard';
import {
  CardsWrapperStyled,
  CONFIRM_POPUP_STYLE,
  ConfirmPopupContentStyled,
  FIELD_STYLES,
  PopupControlsWrapperStyled,
  ServerErrorStyled,
} from './PaymentCard.styled';

const CARDS = [
  { name: 'Visa', slug: 'visa' },
  { name: 'Master Card', slug: 'mastercard' },
  { name: 'Maestro', slug: 'maestro' },
  { name: 'American Express', slug: 'americanexpress' },
  { name: 'JCB', slug: 'jcb' },
  { name: 'Discover', slug: 'discover' },
  { name: 'Dinner Club', slug: 'dinnerclub' },
  { name: 'Union Pay', slug: 'unionpay' },
  { name: 'Google Pay', slug: 'googlepay' },
  { name: 'Apple Pay', slug: 'applepay' },
];

const PaymentForm: React.FC<PaymentCardPropsT> = ({
  isBreadcrumbClicked,
  sustainableActions,
  carbonOffsets,
}) => {
  const {
    isConfirmPopupOpen,
    isLoadingPayment,
    errors,
    serverError,
    setErrors,
    handleOnPurchase,
    setIsConfirmPopupOpen,
  } = usePaymentForm(sustainableActions, carbonOffsets);

  const elementOptions = useMemo(
    () => ({
      style: FIELD_STYLES,
    }),
    []
  );
  const isPurchaseDisabled =
    Object.values(errors).some(error => !error.complete || !!error.errorMessage) ||
    isLoadingPayment;

  const navigate = useNavigate();

  useEffect(() => {
    if (isBreadcrumbClicked && !isPurchaseDisabled) {
      handleOnPurchase();
    }
  }, [isBreadcrumbClicked]);

  return (
    <>
      <FormRowStyled>
        <FieldWrapperStyled>
          <InputLabelStyled>
            <FormattedMessage id="payment.supportedCards" defaultMessage="Cards we support" />
          </InputLabelStyled>

          <CardsWrapperStyled>
            {CARDS.map(({ name, slug }, index) => (
              <img key={index} src={`/images/icons/cards/${slug}.svg`} alt={name} />
            ))}
          </CardsWrapperStyled>
        </FieldWrapperStyled>
      </FormRowStyled>

      <FormRowStyled>
        <FieldWrapperStyled>
          <InputLabelStyled>
            <FormattedMessage id="payment.cardNumber" defaultMessage="Credit card number" />
          </InputLabelStyled>
          <CardNumberElement
            options={elementOptions}
            onChange={val => {
              setErrors({
                ...errors,
                cardNumber: { complete: val.complete, errorMessage: val.error?.message },
              });
            }}
          />

          <ErrorMessageStyled>{errors.cardNumber.errorMessage}</ErrorMessageStyled>
        </FieldWrapperStyled>
      </FormRowStyled>

      <FormRowStyled>
        <FieldWrapperStyled>
          <InputLabelStyled>
            <FormattedMessage id="payment.valid" defaultMessage="Valid Thru" />
          </InputLabelStyled>
          <CardExpiryElement
            options={elementOptions}
            onChange={val => {
              setErrors({
                ...errors,
                cardExpiry: { complete: val.complete, errorMessage: val.error?.message },
              });
            }}
          />

          <ErrorMessageStyled>{errors.cardExpiry.errorMessage}</ErrorMessageStyled>
        </FieldWrapperStyled>

        <FieldWrapperStyled>
          <InputLabelStyled>
            <FormattedMessage id="payment.cvc" defaultMessage="CVC" />
          </InputLabelStyled>
          <CardCvcElement
            options={elementOptions}
            onChange={val => {
              setErrors({
                ...errors,
                cvc: { complete: val.complete, errorMessage: val.error?.message },
              });
            }}
          />

          <ErrorMessageStyled>{errors.cvc.errorMessage}</ErrorMessageStyled>
        </FieldWrapperStyled>
      </FormRowStyled>

      {serverError && <ServerErrorStyled>{serverError}</ServerErrorStyled>}

      <ControlsWrapperStyled>
        <Button isFullWidth disabled={isLoadingPayment} onClick={() => navigate(-1)}>
          <FormattedMessage id="common.cancel" defaultMessage="Cancel" />
        </Button>
        <Button
          isInverted
          isFullWidth
          disabled={isPurchaseDisabled}
          onClick={() => setIsConfirmPopupOpen(true)}
        >
          <FormattedMessage id="common.purchase" defaultMessage="Purchase" />
        </Button>
      </ControlsWrapperStyled>

      <Popup
        contentStyle={CONFIRM_POPUP_STYLE}
        isOpen={isConfirmPopupOpen}
        onClose={() => setIsConfirmPopupOpen(false)}
      >
        <ConfirmPopupContentStyled>
          <Heading level={3} align="center" color="#000">
            <FormattedMessage id="payment.confirmMessage" defaultMessage="Please confirm payment" />
          </Heading>
          <PopupControlsWrapperStyled>
            <Button onClick={() => setIsConfirmPopupOpen(false)}>
              <FormattedMessage id="common.cancel" defaultMessage="Cancel" />
            </Button>

            <Button isInverted isLoading={isLoadingPayment} onClick={handleOnPurchase}>
              <FormattedMessage id="common.confirm" defaultMessage="Confirm" />
            </Button>
          </PopupControlsWrapperStyled>
        </ConfirmPopupContentStyled>
      </Popup>
    </>
  );
};

export default PaymentForm;
